import { Trans, useLingui } from '@lingui/react'
import { clsx } from 'clsx'
import { bool, shape } from 'prop-types'
import { stringifyUrl } from 'query-string'
import { compose } from 'redux'

import { GTM_EVENT_CLICK_PRODUCT_BUTTON, TENANT_CH } from 'shared/consts'
import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import {
  GTM_EVENT_CLICK_PRODUCT,
  GTM_EVENT_CLICK_PRODUCT_IMAGE,
  GTM_EVENT_CLICK_PRODUCT_OFFERS,
} from 'shared/consts/gtm'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { PRICE_TYPES } from 'shared/consts/productTemplates'
import {
  isMarketplace,
  isVariantAttributes,
} from 'shared/experiments/utils/featureFlags'
import { isCdnUrl } from 'shared/utils/imageUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import {
  getPriceForType,
  getProductTemplate,
} from 'shared/utils/productTemplateUtils'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import Price from 'views/components/atoms/Price'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import StarRating from 'views/components/molecules/StarRating'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import ProductAction from 'views/components/organisms/FilteredProductList/SearchProductListItem/ProductAction'
import { useProductUrl } from 'views/components/organisms/FilteredProductList/useProductUrl'
import useVariantState from 'views/components/organisms/FilteredProductList/useVariantState'
import ProductStatus from 'views/components/organisms/ProductStatus'
import SellerBox from 'views/components/organisms/SellerBox'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withMountPoints from 'views/enhancers/withMountPoints'
import { withSearchStateAndUrlContext } from 'views/enhancers/withSearchStateAndUrlContext'
import { getDiscountToShow } from 'views/utils/discount'
import { isRedPointsActive } from 'views/utils/featureToggleUtils'
import {
  hasMultipleVariantAttributes,
  isDiscountValid,
  isListPriceGreaterThanOrEqualToRetailPrice,
  isPricePresent,
  isProductAvailable,
  isProductOutOfStock,
  isSingleVariantAttributeSERP,
} from 'views/utils/productUtils'
import { mountPointsType, ProductType } from 'views/utils/propTypes'

import {
  collectLabelsProps,
  getDummyRedpointsData,
  shouldRenderProductLabels,
} from './helpers'
import IdSwitcher from './IdSwitcher'
import ProductVariants from './ProductVariants'
import SearchProductLabels from './SearchProductLabels'
import './SearchProductListItem.scss'
import SearchProductListItemComponent from './SearchProductListItemComponent'

const SearchProductListItem = props => {
  const { i18n } = useLingui()

  const { publicRuntimeConfig } = useShopAndRuntimeConfig()

  const {
    enableSponsoredLabel,
    hideStockStatusOnMobile,
    isFirst,
    lazyLoadImage,
    product,
    queryParams,
    shouldShowVariantButtonsOnHits,
    showDiscountLabel,
    showVolumePricingLabel,
  } = props

  const {
    flags: { prescriptionMedicine },
    highlighted,
    highlightedProductLabel,
    prescriptionType,
    rating,
    ratingCount,
    redPoints,
    redPointsIsActive,
    redPointsMultiplierIsValid,
    sponsoredProductLabel,
    srcHeight,
    srcWidth,
    thumbnail,
    variants,
    metadata,
  } = product

  const { pageProperties, publicConfig } = publicRuntimeConfig

  const { experiments, featureToggles, isSubscriptionProduct, pageType } =
    pageProperties

  const { discountBoundaries, tenant } = publicConfig

  const isRxCh = tenant === TENANT_CH && prescriptionMedicine

  const variantAttributesEnabled = isVariantAttributes(experiments)

  const [variantState, setVariantState] = useVariantState(product)

  const {
    bestOffer,
    currentVariantId,
    descriptionShort,
    discount,
    displayCode,
    ean,
    imageUrl,
    isbn,
    multiplierRP,
    name,
    objectID,
    ods_partners,
    offerTypeSae,
    offers,
    packageDetails,
    priceInfo,
    pzn,
    sellerBoxParams,
    sellerCount,
    upid,
  } = variantState

  const isItemWithMultipleVariantAttributes = hasMultipleVariantAttributes(
    variants,
    experiments
  )

  const shouldShowProductVariantsBox = () => {
    const shouldShowVariants =
      shouldShowVariantButtonsOnHits && product.variants?.length > 1

    if (variantAttributesEnabled) {
      return (
        shouldShowVariants && isSingleVariantAttributeSERP(product.variants)
      )
    }

    return shouldShowVariants
  }

  const hasSearchProductAddToCartButton = (
    stockStatus,
    { count = 0, currentOfferId },
    tenant
  ) => {
    // We show RX products in CH, even if they are not available in stock. The users will be redirected from PDP to Mediservice
    if (isRxCh) {
      return true
    }

    const hasMarketplaceEnabledWithOffersAvailable =
      isMarketplace(experiments, tenant) && !(count === 1 && currentOfferId)

    if (
      hasMarketplaceEnabledWithOffersAvailable ||
      isItemWithMultipleVariantAttributes
    ) {
      return false
    }

    return !prescriptionMedicine && !isProductOutOfStock(stockStatus)
  }

  const adServerMetadataContent = metadata || {}

  const getProductUrl = useProductUrl({
    adServerMetadataContent,
    product,
    queryParams,
    variantState,
  })

  const stockStatus = priceInfo.stockStatus ?? product.stockStatus

  const getRenderConditionals = ({ listPrice, retailPrice }) => {
    const {
      nowProductGroup,
      offers: { count: offersCount },
      priceInfo: { stockStatus = product.stockStatus },
      type,
    } = variantState

    const hasOffers = offersCount > 1
    let shouldDisplayNowWidget = nowProductGroup === 'sameday_nextday'
    if (shouldDisplayNowWidget && isMarketplace(experiments, tenant)) {
      shouldDisplayNowWidget = type === OFFER_TYPE_SAE
    }

    const isListPricePresent =
      !prescriptionMedicine &&
      isListPriceGreaterThanOrEqualToRetailPrice({
        listPrice,
        retailPrice,
      })

    const shouldDisplayLinkToOfferPage =
      !isItemWithMultipleVariantAttributes &&
      hasOffers &&
      isMarketplace(experiments, tenant)

    const shouldShowPriceStartingFrom =
      !shouldShowProductVariantsBox() && product.variants?.length > 1

    return {
      hasAddToCartButton: hasSearchProductAddToCartButton(
        stockStatus,
        offers,
        tenant
      ),
      isAvailable: isProductAvailable(stockStatus),
      isListPricePresent,
      isRetailPricePresent: isPricePresent(retailPrice),
      isValidDiscount: isDiscountValid(discount, discountBoundaries),
      shouldDisplayLinkToOfferPage,
      shouldShowPriceStartingFrom,
      shouldDisplayNowWidget,
      shouldDisplayRatings: !prescriptionMedicine && ratingCount > 0,
      shouldShowSellerBox: offersCount === 1,
      isRedPointsFeatureToggleActive: isRedPointsActive(featureToggles),
    }
  }

  const currentVariant = variants.find(
    variant => variant.id === currentVariantId
  )

  const productTemplate = getProductTemplate({
    flags: product.flags,
    tenant,
  })

  const listPrice = getPriceForType(priceInfo.prices, PRICE_TYPES.LIST)

  const retailPrice = getPriceForType(priceInfo.prices, PRICE_TYPES.RETAIL)

  const {
    hasAddToCartButton,
    isAvailable,
    isListPricePresent,
    isRedPointsFeatureToggleActive,
    isRetailPricePresent,
    isValidDiscount,
    shouldDisplayLinkToOfferPage,
    shouldShowPriceStartingFrom,
    shouldDisplayRatings,
    shouldShowSellerBox,
  } = getRenderConditionals({ listPrice, retailPrice })

  let redPointsData
  if (isRedPointsFeatureToggleActive && multiplierRP) {
    redPointsData = getDummyRedpointsData(multiplierRP)
  }

  const supNoteNumber = publicConfig.superScripts?.supNoteNumber
  const discountLabel = (
    <>
      <span>&minus;{discount}%</span>
      {supNoteNumber && <sup>{supNoteNumber}</sup>}
    </>
  )
  const labelsProps = collectLabelsProps(
    {
      ...product,
      ...currentVariant,
      objectID,
    },
    enableSponsoredLabel,
    publicRuntimeConfig,
    adServerMetadataContent,
    showDiscountLabel && isValidDiscount,
    Boolean(product.hasBestOfferSellerPromotion)
  )

  const offersUrl = stringifyUrl({
    url: getProductUrl(GTM_EVENT_CLICK_PRODUCT_OFFERS),
    query: {
      offerSelectionDrawer: true,
    },
  })

  /**
   * It is convenient to define react components as constants below here,
   * because they are small and there are a lot of them. It is easier to keep
   * them in one file, as split to many small files and hand over plenty of properties
   * every time
   */
  const renderProductItemLinkWithIcon = (
    <>
      {isItemWithMultipleVariantAttributes && (
        <div className="o-PointerOnHover">
          <LinkWithIcon
            href={getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
            icon={<IconArrowRight className="u-color--blue" />}
            right
          >
            <TransWithHtml
              className="u-font-weight--bold u-color--blue u-no-wrap o-PointerOnHover__text"
              text={i18n._('product.variants.configure.link')}
            />
          </LinkWithIcon>
        </div>
      )}
      {shouldDisplayLinkToOfferPage && (
        <div className="o-PointerOnHover">
          <LinkWithIcon
            href={offersUrl}
            icon={<IconArrowRight />}
            className="u-font-weight--bold o-PointerOnHover__text"
            right
          >
            {product.hasNonBestOfferSellerPromotion ? (
              <span className="o-PointerOnHover__text">
                <span
                  className="u-font-style--italic"
                  data-qa-id="serp-has-non-best-offer-seller-promotion-label"
                >
                  <Trans id="seller.promotion.product.badge" /> <wbr />
                </span>
                <span className="u-no-wrap">
                  <Trans
                    id="product.seller.count.lowercase"
                    values={{ count: sellerCount }}
                  />
                </span>
              </span>
            ) : (
              <span className="u-no-wrap o-PointerOnHover__text">
                <Trans
                  id="product.seller.count"
                  values={{ count: sellerCount }}
                />
              </span>
            )}
          </LinkWithIcon>
        </div>
      )}
    </>
  )

  const renderProductVariantsBox = shouldShowProductVariantsBox() && (
    <ProductVariants
      currentVariantId={currentVariantId}
      updateCurrentVariant={setVariantState}
      variants={variants}
    />
  )

  const searchProductListItemProps = {
    'data-clientside-hook': 'FilteredProductListItem',
    'data-issubscriptionproduct': isSubscriptionProduct,
    'data-qa-id': 'result-list-entry',
    [AD_SERVER_METADATA_DATA_ATTR]: !isEmpty(adServerMetadataContent)
      ? JSON.stringify(adServerMetadataContent || {})
      : undefined,
  }

  const listPriceWithDiscount = isListPricePresent && isValidDiscount && (
    <p className="l-flex u-no-margin o-SearchProductListItem__prices__list-price-wrapper">
      <div className="o-SearchProductListItem__prices__list-price__text">
        {offerTypeSae ? (
          <Trans id={listPrice?.info?.text} />
        ) : (
          <Trans id="general.label.miraklListPrice" />
        )}
        <sup>{listPrice?.info?.index}</sup>
      </div>
      <span className="u-margin-xx-small--left o-SearchProductListItem__prices__list-price u-text-decoration--line-through">
        {offerTypeSae ? (
          <Price {...listPrice} />
        ) : (
          bestOffer?.strike_price?.formatted
        )}
      </span>
    </p>
  )

  const retailPriceComponent = isRetailPricePresent && (
    <p
      className={clsx(
        'u-no-margin o-SearchProductListItem__prices__retail-price a-h2',
        {
          'u-color--red': isValidDiscount,
        }
      )}
      data-qa-id="entry-price"
    >
      {shouldShowPriceStartingFrom && (
        <>
          <Trans id="general.label.price.from" />
          &nbsp;
        </>
      )}
      <Price {...retailPrice} />
    </p>
  )

  const idSwitcher = (
    <li className="u-hidden@small-tiny o-SearchProductListItem__info-list__item">
      <span
        className="a-h4-tiny o-SearchProductListItem__info__items"
        suppressHydrationWarning
      >
        <IdSwitcher
          ean={ean}
          isbn={isbn}
          pzn={displayCode}
          uid={packageDetails.id}
          upid={upid}
        />
      </span>
    </li>
  )

  const productUrl = getProductUrl(GTM_EVENT_CLICK_PRODUCT)

  const productStatus = !isRxCh && (
    <ProductStatus
      className={clsx('a-h4-tiny u-margin-small--bottom@small-tiny', {
        'u-hidden@small-tiny': hideStockStatusOnMobile && isAvailable,
      })}
      dot
      stockStatus={stockStatus}
    />
  )

  const unitPricingComponent = priceInfo.unitPricing && (
    <p className="u-no-margin u-margin-xx-small--bottom u-no-wrap u-color--grey o-SearchProductListItem__prices__unitPricing">
      {priceInfo.unitPricing}
    </p>
  )

  const ctaViewPdpRxCh = (
    <LinkWithIcon
      href={productUrl}
      icon={<IconArrowRight />}
      right
      className="u-font-weight--bold a-full-width"
    >
      <Trans id="general.link.label.recipe" />
    </LinkWithIcon>
  )

  const addToCartButtonComponent = (
    <ProductAction
      algoliaQueryId={queryParams.queryID}
      algoliaQuery={queryParams.query}
      hasAddToCartButton={hasAddToCartButton}
      mobile
      offerId={offers.currentOfferId}
      position={queryParams.position}
      prescriptionType={prescriptionType}
      product={product}
      productId={currentVariantId}
      productTemplate={productTemplate}
      productUrl={getProductUrl(GTM_EVENT_CLICK_PRODUCT_BUTTON)}
      stockStatus={stockStatus}
      trackingUrls={adServerMetadataContent.trackingUrls}
    />
  )

  const starRatingComponent = shouldDisplayRatings && (
    <StarRating
      hasSuperScript
      rating={rating}
      ratingCount={ratingCount}
      ratingCountColor="dark"
    />
  )

  const searchProductLabelsComponent = (
    <SearchProductLabels
      {...labelsProps}
      discountLabel={discountLabel}
      sponsoredProductLabel={sponsoredProductLabel}
      showVolumePricingLabel={showVolumePricingLabel}
      product={product}
    />
  )

  const odsPartnersComponent = ods_partners &&
    ods_partners.length > 0 &&
    publicConfig.onlineDoctorConfig?.enabled && (
      <div
        className="odsModalbuttonDiv u-color--blue u-padding-xx-large--bottom u-font-weight--bold"
        data-clientside-hook="ods-modal-trigger-buttonSERP"
        data-odspartners={`odsModalProduct-${JSON.stringify(ods_partners)}`}
        id="odsModalButtonSERP"
      >
        <ModalShowButton
          component={ButtonAsLink}
          id="odsModalButton"
          modalId={`modalIdOdsSERP`}
          name="buybox-large_online-doctor-service"
          onClick={() => {}}
        >
          <TextWithIcon
            icon={<IconArrowRight className="u-color--blue" />}
            qaId="show-ods-modal-button-serp"
            right
          >
            <Trans id="buyBox.link.label.onlineDoctorService.zava" />
          </TextWithIcon>
        </ModalShowButton>
      </div>
    )

  const discountBadge =
    !showDiscountLabel && getDiscountToShow({ discount, tenant }) ? (
      <DiscountBadge
        className="u-margin-small--right u-font-weight--bold"
        discount={discount}
        ellipse
        rounded
        smaller
      >
        <span className="u-no-wrap" suppressHydrationWarning>
          &minus;{discount}%
        </span>
      </DiscountBadge>
    ) : null

  return (
    <SearchProductListItemComponent
      addToCartButtonComponent={
        isRxCh ? ctaViewPdpRxCh : addToCartButtonComponent
      }
      className={clsx(
        'o-SearchProductListItem u-padding--top u-background-color--background u-position--relative u-border u-border-radius',
        {
          'o-SearchProductListItem--first':
            isFirst && shouldRenderProductLabels(labelsProps),
          'o-SearchProductListItem__highlighted': highlighted,
        }
      )}
      currentOfferId={offers.currentOfferId}
      descriptionShort={descriptionShort}
      discountBadge={discountBadge}
      highlighted={highlighted}
      highlightedProductLabel={highlightedProductLabel}
      idSwitcher={idSwitcher}
      imageProps={{
        discount,
        imageUrl,
        multiplierRP,
        name,
        prescriptionMedicine,
        prescriptionType,
        prices: priceInfo.prices,
        productTemplate,
        redPoints,
        redPointsIsActive,
        redPointsMultiplierIsValid,
        srcHeight,
        srcWidth,
        thumbnail,
      }}
      isAvailable={isAvailable}
      isCdnUrl={isCdnUrl(publicConfig, imageUrl)}
      isItemWithMultipleVariantAttributes={isItemWithMultipleVariantAttributes}
      isRxCh={isRxCh}
      lazyLoadImage={lazyLoadImage}
      listPriceWithDiscount={listPriceWithDiscount}
      manufacturer={packageDetails.manufacturer}
      odsPartnersComponent={odsPartnersComponent}
      pageType={pageType}
      pharmaForm={packageDetails.pharmaForm}
      productStatus={productStatus}
      productUrl={productUrl}
      productUrlWithImageEvent={getProductUrl(GTM_EVENT_CLICK_PRODUCT_IMAGE)}
      pzn={pzn}
      quantityUnit={`${packageDetails.unitQuantityString} ${packageDetails.unit}`}
      redpointsMultiplier={redPointsData?.multiplierRP}
      renderProductItemLinkWithIcon={renderProductItemLinkWithIcon}
      renderProductVariantsBox={renderProductVariantsBox}
      retailPriceComponent={retailPriceComponent}
      searchProductLabelsComponent={searchProductLabelsComponent}
      searchProductListItemProps={searchProductListItemProps}
      sellerBoxComponent={
        shouldShowSellerBox &&
        isMarketplace(experiments, tenant) && (
          <SellerBox
            {...sellerBoxParams}
            cssBoxClasses={{
              backgroundColor: 'u-background-color--greySky',
              padding: 'u-padding-small',
              textAlign: 'u-text-align--left@medium',
            }}
            embedSellerModal={false}
          />
        )
      }
      starRatingComponent={starRatingComponent}
      textInfoClassName={clsx(
        'u-padding--left l-flex l-flex--vertical-stretch u-border--left@medium l-col--12-of-12 l-col--5-of-12@medium o-SearchProductListItem__content__body',
        {
          'l-flex--distribute-space': descriptionShort,
          'l-flex--justify-content--end': !descriptionShort,
        }
      )}
      titleClassName={clsx(
        'o-SearchProductListItem__title u-font-weight--bold u-no-margin a-h3@medium',
        {
          'u-margin-xx-small--bottom': shouldDisplayRatings,
        }
      )}
      unitPricingComponent={unitPricingComponent}
    />
  )
}

SearchProductListItem.propTypes = {
  enableSponsoredLabel: bool,
  hideStockStatusOnMobile: bool,
  isFirst: bool,
  lazyLoadImage: bool.isRequired,
  mountPoints: mountPointsType.isRequired,
  product: shape(ProductType).isRequired,
  queryParams: shape({}),
  shouldShowVariantButtonsOnHits: bool,
  showDiscountLabel: bool,
  showVolumePricingLabel: bool,
}

SearchProductListItem.defaultProps = {
  enableSponsoredLabel: true,
  hideStockStatusOnMobile: false,
  isFirst: false,
  queryParams: {},
  shouldShowVariantButtonsOnHits: true,
  showDiscountLabel: false,
  showVolumePricingLabel: false,
}

export default compose(
  withSearchStateAndUrlContext,
  withMountPoints
)(SearchProductListItem)
